/**
 * dictationTarget.ts — remembers which field the person last used and puts dictated text back at their cursor.
 * The microphone is a button, so by the time the text arrives the field has lost focus; this keeps the place.
 */
import { dictationPrefix, fitToLimit } from "@demandhub-cms/shared";

export interface DictationTarget {
  el: HTMLElement;
  /** Cursor position for inputs and textareas. */
  start?: number;
  end?: number;
  /** Cursor position for rich-text editors. */
  range?: Range;
}

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "url", "tel"]);

export function isDictatable(el: EventTarget | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.disabled && !el.readOnly;
  if (el instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(el.type) && !el.disabled && !el.readOnly;
  return el.isContentEditable;
}

/** The outermost editable ancestor, so a click inside a paragraph of a rich-text editor still means "that editor". */
function editableRoot(el: HTMLElement): HTMLElement {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el;
  let root = el;
  while (root.parentElement?.isContentEditable) root = root.parentElement;
  return root;
}

let last: DictationTarget | null = null;

function remember(el: HTMLElement) {
  const root = editableRoot(el);
  if (root instanceof HTMLInputElement || root instanceof HTMLTextAreaElement) {
    last = { el: root, start: root.selectionStart ?? root.value.length, end: root.selectionEnd ?? root.value.length };
    return;
  }
  const selection = window.getSelection();
  last = {
    el: root,
    range: selection && selection.rangeCount > 0 && root.contains(selection.anchorNode) ? selection.getRangeAt(0).cloneRange() : undefined,
  };
}

/** Starts following the person's cursor. Returns a function that stops. */
export function trackDictationTarget(): () => void {
  const onFocus = (e: FocusEvent) => {
    if (isDictatable(e.target)) remember(e.target);
  };
  const onSelection = () => {
    const active = document.activeElement;
    if (isDictatable(active)) remember(active);
  };
  document.addEventListener("focusin", onFocus);
  document.addEventListener("selectionchange", onSelection);
  return () => {
    document.removeEventListener("focusin", onFocus);
    document.removeEventListener("selectionchange", onSelection);
  };
}

/** Where dictated text would go right now, or null when no field has been used (or it has left the page). */
export function currentDictationTarget(): DictationTarget | null {
  return last && last.el.isConnected && isDictatable(last.el) ? { ...last } : null;
}

export type InsertResult = "inserted" | "truncated" | "failed";

export function insertDictation(target: DictationTarget, text: string): InsertResult {
  const { el } = target;
  if (!el.isConnected) return "failed";
  el.focus({ preventScroll: false });

  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const start = Math.min(target.start ?? el.value.length, el.value.length);
    const end = Math.min(target.end ?? start, el.value.length);
    const prefix = dictationPrefix(el.value[start - 1], text);
    const room = el.maxLength >= 0 ? el.maxLength - (el.value.length - (end - start)) : Infinity;
    const fitted = fitToLimit(prefix + text, room);
    if (fitted.text === "") return "failed";
    el.setRangeText(fitted.text, start, end, "end");
    el.dispatchEvent(new Event("input", { bubbles: true }));
    return fitted.cut ? "truncated" : "inserted";
  }

  const selection = window.getSelection();
  if (!selection) return "failed";
  if (target.range && el.contains(target.range.startContainer)) {
    selection.removeAllRanges();
    selection.addRange(target.range);
  }
  const before = selection.anchorNode?.textContent?.[selection.anchorOffset - 1];
  return document.execCommand("insertText", false, dictationPrefix(before, text) + text) ? "inserted" : "failed";
}

/** Undoes the last typed change in the field the person was using. */
export function undoInTarget(target: DictationTarget | null): boolean {
  if (!target || !target.el.isConnected) return false;
  target.el.focus();
  return document.execCommand("undo");
}

/** A short plain name for a field, for "Typing into: …". Uses what a person would call it, never the HTML id. */
export function describeTarget(el: HTMLElement): string {
  const clean = (t: string | null | undefined) => (t ?? "").replace(/\s+/g, " ").trim().slice(0, 40);
  const aria = clean(el.getAttribute("aria-label"));
  if (aria) return aria;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const label = clean(el.labels?.[0]?.textContent);
    if (label) return label;
    const placeholder = clean(el.placeholder);
    if (placeholder) return placeholder;
  }
  return el.isContentEditable ? "the editor" : "this field";
}
